import forge from 'node-forge/lib/forge.js'
import 'node-forge/lib/aes.js'

const ENCRYPT_KEY_STORAGE_KEY = 'encryptKey'
const AES_GCM_IV_LENGTH = 12
const AES_GCM_TAG_LENGTH = 16

let memoryEncryptKey = ''
let cachedEncryptKey = ''
let cachedCryptoProvider = null
let cachedCryptoKeyPromise = null

function getLocalStorage() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage
  } catch {
    // 隐私模式等环境可能禁止访问 localStorage，继续使用内存密钥。
  }
  return null
}

function getStoredEncryptKey() {
  const storage = getLocalStorage()
  if (storage) {
    try {
      const encryptKey = storage.getItem(ENCRYPT_KEY_STORAGE_KEY)
      if (encryptKey) return encryptKey
    } catch {
      // 读取失败时使用当前页面生命周期内的内存密钥。
    }
  }
  return memoryEncryptKey
}

export function setResponseEncryptKey(encryptKey) {
  if (typeof encryptKey !== 'string' || !encryptKey || getStoredEncryptKey() === encryptKey) return

  memoryEncryptKey = encryptKey
  const storage = getLocalStorage()
  if (storage) {
    try {
      storage.setItem(ENCRYPT_KEY_STORAGE_KEY, encryptKey)
    } catch {
      // localStorage 不可用时，保留内存密钥供当前会话使用。
    }
  }
  resetCachedCryptoKey()
}

function resetCachedCryptoKey() {
  cachedEncryptKey = ''
  cachedCryptoProvider = null
  cachedCryptoKeyPromise = null
}

export function clearResponseEncryptKey() {
  memoryEncryptKey = ''
  resetCachedCryptoKey()

  const storage = getLocalStorage()
  if (storage) {
    try {
      storage.removeItem(ENCRYPT_KEY_STORAGE_KEY)
    } catch {
      // localStorage 不可用时，内存密钥已完成清理。
    }
  }
}

export function hasResponseEncryptKey() {
  return Boolean(getStoredEncryptKey())
}

function getResponseEncryptKey(responseData) {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) return ''
  if (typeof responseData.encryptKey === 'string') return responseData.encryptKey
  if (
    responseData.data &&
    typeof responseData.data === 'object' &&
    !Array.isArray(responseData.data) &&
    typeof responseData.data.encryptKey === 'string'
  ) {
    return responseData.data.encryptKey
  }
  return ''
}

function saveResponseEncryptKey(responseData) {
  const encryptKey = getResponseEncryptKey(responseData)
  setResponseEncryptKey(encryptKey)
}

function decodeBase64(value) {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length % 4 !== 0 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(value)
  ) {
    return null
  }

  try {
    const binary = typeof atob === 'function' ? atob(value) : forge.util.decode64(value)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index)
    }
    return bytes
  } catch {
    return null
  }
}

function encodeUtf8(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value)

  const encodedValue = encodeURIComponent(value)
  const bytes = []
  for (let index = 0; index < encodedValue.length; index += 1) {
    if (encodedValue[index] === '%') {
      bytes.push(parseInt(encodedValue.slice(index + 1, index + 3), 16))
      index += 2
    } else {
      bytes.push(encodedValue.charCodeAt(index))
    }
  }
  return new Uint8Array(bytes)
}

function decodeUtf8(value) {
  if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(value)

  let encodedValue = ''
  for (let index = 0; index < value.length; index += 1) {
    const hex = value[index].toString(16)
    encodedValue += `%${hex.length === 1 ? `0${hex}` : hex}`
  }
  return decodeURIComponent(encodedValue)
}

function bytesToBinaryString(value) {
  const chunks = []
  const chunkSize = 8192
  for (let offset = 0; offset < value.length; offset += chunkSize) {
    let chunk = ''
    const end = Math.min(offset + chunkSize, value.length)
    for (let index = offset; index < end; index += 1) {
      chunk += String.fromCharCode(value[index])
    }
    chunks.push(chunk)
  }
  return chunks.join('')
}

function binaryStringToBytes(value) {
  const bytes = new Uint8Array(value.length)
  for (let index = 0; index < value.length; index += 1) {
    bytes[index] = value.charCodeAt(index)
  }
  return bytes
}

function getEncryptKeyBytes(encryptKey) {
  const keyBytes = encodeUtf8(encryptKey)
  if (keyBytes.byteLength !== 32) {
    throw new Error('响应解密失败：encryptKey 必须为 32 字节')
  }
  return keyBytes
}

function getCryptoProvider() {
  if (typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) {
    return globalThis.crypto
  }
  if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
    return window.crypto
  }
  return null
}

function getCryptoKey(cryptoProvider, encryptKey, keyBytes) {
  if (
    cachedCryptoKeyPromise &&
    cachedEncryptKey === encryptKey &&
    cachedCryptoProvider === cryptoProvider
  ) {
    return cachedCryptoKeyPromise
  }

  cachedEncryptKey = encryptKey
  cachedCryptoProvider = cryptoProvider
  cachedCryptoKeyPromise = cryptoProvider.subtle
    .importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt'])
    .catch((error) => {
      if (cachedEncryptKey === encryptKey && cachedCryptoProvider === cryptoProvider) {
        resetCachedCryptoKey()
      }
      throw error
    })
  return cachedCryptoKeyPromise
}

async function decryptWithWebCrypto(encryptedBytes, encryptKey, keyBytes) {
  const cryptoProvider = getCryptoProvider()
  if (!cryptoProvider) return null

  const iv = encryptedBytes.subarray(0, AES_GCM_IV_LENGTH)
  const ciphertext = encryptedBytes.subarray(AES_GCM_IV_LENGTH)

  try {
    const cryptoKey = await getCryptoKey(cryptoProvider, encryptKey, keyBytes)
    const decryptedBuffer = await cryptoProvider.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: AES_GCM_TAG_LENGTH * 8 },
      cryptoKey,
      ciphertext,
    )
    return new Uint8Array(decryptedBuffer)
  } catch {
    return null
  }
}

function decryptWithForge(encryptedBytes, keyBytes) {
  const tagStart = encryptedBytes.length - AES_GCM_TAG_LENGTH
  const iv = encryptedBytes.subarray(0, AES_GCM_IV_LENGTH)
  const ciphertext = encryptedBytes.subarray(AES_GCM_IV_LENGTH, tagStart)
  const tag = encryptedBytes.subarray(tagStart)

  const decipher = forge.cipher.createDecipher('AES-GCM', bytesToBinaryString(keyBytes))
  decipher.start({
    iv: bytesToBinaryString(iv),
    tagLength: AES_GCM_TAG_LENGTH * 8,
    tag: forge.util.createBuffer(bytesToBinaryString(tag)),
  })
  decipher.update(forge.util.createBuffer(bytesToBinaryString(ciphertext)))
  if (!decipher.finish()) return null

  return binaryStringToBytes(decipher.output.getBytes())
}

async function tryDecryptData(encryptedBytes, encryptKey) {
  const keyBytes = getEncryptKeyBytes(encryptKey)
  const nativeDecryptedBytes = await decryptWithWebCrypto(encryptedBytes, encryptKey, keyBytes)
  const decryptedBytes = nativeDecryptedBytes || decryptWithForge(encryptedBytes, keyBytes)
  if (!decryptedBytes) return { encrypted: false }

  return {
    encrypted: true,
    value: JSON.parse(decodeUtf8(decryptedBytes)),
  }
}

function isTableData(value) {
  return (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray(value.rows) &&
    Object.prototype.hasOwnProperty.call(value, 'total')
  )
}

function getEncryptedResponseBytes(responseData) {
  if (
    !responseData ||
    typeof responseData !== 'object' ||
    Array.isArray(responseData) ||
    typeof responseData.data !== 'string'
  ) {
    return null
  }

  const encryptedBytes = decodeBase64(responseData.data)
  if (!encryptedBytes || encryptedBytes.byteLength < AES_GCM_IV_LENGTH + AES_GCM_TAG_LENGTH) {
    return null
  }
  return encryptedBytes
}

export function hasEncryptedResponseData(responseData) {
  return Boolean(
    responseData &&
    Object.prototype.hasOwnProperty.call(responseData, 'code') &&
    Object.prototype.hasOwnProperty.call(responseData, 'msg') &&
    getEncryptedResponseBytes(responseData)
  )
}

export async function normalizeEncryptedResponse(responseData) {
  if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
    return responseData
  }

  saveResponseEncryptKey(responseData)

  const encryptKey = getStoredEncryptKey()
  if (!encryptKey) return responseData

  const encryptedBytes = getEncryptedResponseBytes(responseData)
  if (!encryptedBytes) return responseData

  const decryptedResult = await tryDecryptData(encryptedBytes, encryptKey)
  if (!decryptedResult.encrypted) return responseData

  const normalizedResponse = {
    ...responseData,
    data: decryptedResult.value,
  }
  if (isTableData(decryptedResult.value)) {
    Object.assign(normalizedResponse, decryptedResult.value)
  }
  return normalizedResponse
}
